import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Form,
  Flex,
  Text,
  Divider,
  FormButton,
  ReadonlyField,
  Box,
} from '@shared/components';
import { zodResolver } from '@shared/utils';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useQSetReviewTaskCreation } from '../../providers';
import {
  QSetReviewTaskCreationFormSchema,
  type QSetReviewTaskCreationFormInput,
} from '../../schemas';
import { ExamCourseDropDownSelectionForm } from '../exam-course-drop-down-selection-form';
import { QSetList } from '../qset-list';
import { ReviewQsetTaskDetailsFormFields } from '../qset-review-task-details-form-fields';

export interface QSetReviewTaskCreationFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  examCourse?: { value: string; label: string };
}

export default function QSetReviewTaskCreationFormModal(
  props: Readonly<QSetReviewTaskCreationFormModalProps>,
) {
  const { isOpen, onClose, examCourse } = props;
  const { createQSetReviewTask } = useQSetReviewTaskCreation();
  const formContext = useForm<QSetReviewTaskCreationFormInput>({
    defaultValues: {
      examCourseSelection: {
        examCourse: examCourse ?? undefined,
      },
    },
    resolver: zodResolver(QSetReviewTaskCreationFormSchema),
  });
  const { examCourseSelection, qsetSelection, taskDetails } = formContext.watch() ?? {};
  const qsetId = qsetSelection?.qsetId;

  const [isSelectedExamCourse, setIsSelectedExamCourse] = useState(false);

  const handleCreateQSetReviewTask = async (data: QSetReviewTaskCreationFormInput) => {
    await createQSetReviewTask(data);
    onCloseModal();
  };

  useEffect(() => {
    const examCourseId = formContext.getValues('examCourseSelection.examCourse');
    if (examCourseId) {
      setIsSelectedExamCourse(true);
    } else {
      setIsSelectedExamCourse(false);
    }
    formContext.setValue('qsetSelection.qsetId', []);
  }, [formContext.watch('examCourseSelection.examCourse')]);

  const onCloseModal = () => {
    onClose();
    formContext.reset();
  };
  const isCreateButtonDisabled =
    !taskDetails?.name ||
    !taskDetails?.assignee ||
    !taskDetails?.assignTo ||
    !taskDetails?.dueDate ||
    !examCourseSelection?.examCourse?.value ||
    !qsetSelection?.qsetId?.length;

  return (
    <FormProvider {...formContext}>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth='lg'
        slotProps={{
          paper: {
            component: Form,
            onSubmit: formContext.handleSubmit(handleCreateQSetReviewTask),
          },
        }}
      >
        <DialogTitle onClose={onCloseModal}>Create Task: QSet Review</DialogTitle>
        <Divider />
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: (theme) => theme.spacing(4) }}
        >
          <ReviewQsetTaskDetailsFormFields />
          <Text variant='body1Semibold'>กำหนดชุดข้อสอบที่ต้องการ</Text>
          <Box
            sx={(theme) => ({
              borderRadius: theme.shape.rounded(2),
              border: `1px solid ${theme.palette.grey[200]}`,
              bgcolor: theme.palette.grey[50],
              p: theme.spacing(4),
            })}
          >
            {examCourse?.value ? (
              <ReadonlyField label='เลือกหลักสูตรทดสอบ' content={`${examCourse.label}`} />
            ) : (
              <ExamCourseDropDownSelectionForm name='examCourseSelection.examCourse' />
            )}
          </Box>
          {isSelectedExamCourse ? (
            <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
              <Text variant='body1Semibold'>เลือกแล้ว: {qsetId?.length ?? 0} รายการ</Text>
              <QSetList />
            </Flex>
          ) : null}
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button onClick={onCloseModal} color='secondary' variant='outlined'>
            Cancel
          </Button>
          <FormButton disabled={isCreateButtonDisabled} label='Create Task' />
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
